"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { humanMessage } from "./api";

/*
 * 불러오기 한 번 — 화면마다 반복되는 세 상태(기다림·오류·값)를 한 곳에 둡니다.
 *
 * 🔴 **떠난 화면의 응답으로 상태를 바꾸지 않습니다.** 목록에서 상세로 빠르게
 *    넘어가면 앞 화면의 응답이 뒤늦게 도착하는데, 그것을 그대로 반영하면 방금 연
 *    화면이 옛 내용으로 덮입니다. `AbortController` 로 끊고, 늦게 온 것은 버립니다.
 *
 * 🔑 `key` 로 다시 부를 시점을 정합니다. 함수를 의존성에 넣으면 매 렌더마다
 *    새 함수라 무한히 다시 부릅니다 — 실제로 흔히 밟는 자리입니다.
 */
export type Async<T> = {
  data: T | null;
  error: string;
  loading: boolean;
  /** 다시 불러옵니다 (「다시 시도」 버튼·수정 뒤 갱신) */
  reload: () => void;
};

export function useAsync<T>(
  load: (signal: AbortSignal) => Promise<T>,
  key: string,
  /** 아직 부를 때가 아니면 `false` — 로그인 상태를 기다리는 화면에 씁니다 */
  enabled = true,
): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(enabled);
  const [nonce, setNonce] = useState(0);

  /* 최신 `load` 를 담아 둡니다 — 의존성에 넣지 않기 위해서입니다(위 머리말). */
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let alive = true;

    setLoading(true);
    setError("");

    loadRef
      .current(controller.signal)
      .then((value) => {
        if (!alive) return;
        setData(value);
        setLoading(false);
      })
      .catch((cause) => {
        if (!alive || controller.signal.aborted) return;
        setError(humanMessage(cause));
        setLoading(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [key, enabled, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, error, loading, reload };
}
